import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { t } from '../../i18n';

/**
 * Fenetre accrochee a un bouton.
 *
 * Une feuille qui monte du bas fait perdre le bouton qu'on vient
 * d'appuyer : on regarde en bas ce qu'on a demande en haut. La fenetre
 * accrochee sort de son bouton, ce qui dit d'ou elle vient et a quoi
 * elle se rapporte. C'est la seule forme de fenetre de l'application —
 * les feuilles d'actions passent par `Sheet`, qui n'est plus qu'une
 * facon de la remplir.
 *
 * Elle est posee en `fixed`, hors du flux, pour deux raisons : une zone
 * defilante rogne ce qui deborde d'elle, et un bloc pose en absolu
 * dedans allongerait sa hauteur de defilement. La position est donc
 * calculee a l'ouverture, d'apres le cadre du bouton.
 *
 * Le voile derriere fige la page — sans quoi la fenetre resterait ou on
 * l'a posee pendant que le contenu glisse dessous.
 */

/**
 * Le bouton dont la fenetre sort.
 *
 * Une `ref` quand le declencheur est unique dans l'ecran, l'element
 * lui-meme quand il y en a un par ligne — le menu d'un post ne peut pas
 * tenir une ref par carte, il retient le bouton touche.
 */
export type Ancre = RefObject<HTMLElement> | HTMLElement | null | undefined;

/** Marge minimale gardee entre la fenetre et le bord de l'ecran. */
const MARGE = 12;

/** Distance entre le bouton et la fenetre qui en sort. */
const ECART = 8;

/**
 * En dessous de cette hauteur, ouvrir vers le bas ne montre plus rien
 * d'utile : la fenetre bascule au-dessus du bouton.
 */
const HAUTEUR_UTILE = 220;

/**
 * La largeur d'une fenetre de menu, recopiee de `.popover`.
 *
 * Elle n'est ici que pour borner l'alignement a droite : sans elle, une
 * fenetre accrochee a quelque chose qui n'est pas au bord — une bulle de
 * message recue, posee a gauche du fil — sortait de l'ecran par la
 * gauche. Les deux valeurs doivent rester d'accord.
 */
const LARGEUR = 310;

/**
 * La largeur d'un panneau, et la marge qui va avec — recopiees de
 * `.popover--large`. Les deux valeurs doivent rester d'accord avec la
 * feuille de style, sinon l'alignement calcule ici pousse le panneau hors
 * de l'ecran.
 */
const LARGEUR_PANNEAU = 420;
const MARGE_PANNEAU = 20;

type Place =
  | { haut: number; bas?: undefined; droite: number; hauteurMax: number }
  | { haut?: undefined; bas: number; droite: number; hauteurMax: number }
  /** Faute de bouton connu : au milieu de l'ecran. */
  | 'centre';

function elementDe(ancre: Ancre): HTMLElement | null {
  if (!ancre) return null;
  return 'current' in ancre ? ancre.current : ancre;
}

export function Popover({
  open,
  anchor,
  onClose,
  children,
  label,
  large = false,
}: {
  open: boolean;
  anchor?: Ancre;
  onClose: () => void;
  children: ReactNode;
  /** Ce que la fenetre est, pour qui ne la voit pas. */
  label: string;
  /**
   * Fenetre large et centree : pour ce qui n'est pas un menu — un
   * formulaire, un billet, un scanner. Un tel contenu ne tient pas dans
   * une fenetre de menu accrochee a un bouton.
   */
  large?: boolean;
}) {
  /*
    La position est un etat plutot qu'un calcul au rendu : le cadre du
    bouton n'est connu qu'une fois la page posee, et le lire pendant le
    rendu ferait un premier passage a la mauvaise place.
  */
  const [place, setPlace] = useState<Place | null>(null);

  useEffect(() => {
    if (!open) return setPlace(null);

    /*
      Un panneau s'accroche lui aussi a son bouton, quand il en a un.

      Il partait autrement au milieu de l'ecran : la cloche du fil ouvrait
      sa fenetre loin de l'icone qu'on venait de toucher, et rien ne
      disait plus d'ou elle sortait. Sans bouton connu — un billet, un
      scanner — le centre reste le seul endroit possible.
    */
    const bouton = elementDe(anchor);
    if (!bouton) {
      setPlace('centre');
    } else {
      const cadre = bouton.getBoundingClientRect();
      const dessous = window.innerHeight - cadre.bottom - ECART - MARGE;
      const dessus = cadre.top - ECART - MARGE;
      /*
        Alignee sur le bord droit du bouton : elle s'ouvre vers
        l'interieur de l'ecran, jamais vers le vide. Et jamais au-dela du
        bord gauche, d'ou la seconde borne.
      */
      const marge = large ? MARGE_PANNEAU : MARGE;
      const largeur = Math.min(large ? LARGEUR_PANNEAU : LARGEUR, window.innerWidth - 2 * marge);
      const droite = Math.min(
        Math.max(marge, window.innerWidth - cadre.right),
        window.innerWidth - largeur - marge,
      );
      /*
        Vers le bas tant qu'il y a de quoi lire, vers le haut sinon. Un
        menu ouvert depuis une barre du bas — ou depuis un message en fin
        de fil — sortait autrement sous le pli, hors de l'ecran.
      */
      setPlace(
        dessous >= Math.min(HAUTEUR_UTILE, dessus)
          ? { haut: cadre.bottom + ECART, droite, hauteurMax: dessous }
          : { bas: window.innerHeight - cadre.top + ECART, droite, hauteurMax: dessus },
      );
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.documentElement.classList.add('sheet-open');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.documentElement.classList.remove('sheet-open');
    };
  }, [open, anchor, onClose, large]);

  if (!open || !place) return null;

  const centree = place === 'centre';

  return (
    <div className="popover-root">
      <button className="popover__scrim" aria-label={t.commun.fermer} onClick={onClose} />
      <div
        className={`popover${centree ? ' popover--centre' : ''}${large ? ' popover--large' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={
          centree
            ? undefined
            : {
                top: place.haut,
                bottom: place.bas,
                right: place.droite,
                maxHeight: place.hauteurMax,
                // Elle grandit depuis le coin par lequel elle sort.
                transformOrigin: place.haut === undefined ? 'bottom right' : 'top right',
              }
        }
      >
        {children}
      </div>
    </div>
  );
}
